/**
 * LinkOrphanInvoicesModal Component
 * Modal for linking orphan invoices to jobs/orders
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControlLabel,
  Switch,
  Paper,
  Toolbar,
} from "@mui/material";
import { Close, Link as LinkIcon, CheckCircle, Error as ErrorIcon } from "@mui/icons-material";
import { useDeviceType } from "../../leads/hooks/useDeviceType.js";
import { getOrphanInvoices, bindInvoicesToJobs } from "../services/invoiceBindingService.js";
import OrderSearchAutocomplete from "../../../shared/components/OrderSearchAutocomplete.jsx";
import { logError, logInfo } from "@/shared/utils/logger.js";

/**
 * LinkOrphanInvoicesModal Component
 * @param {Object} props
 * @param {boolean} props.open - Whether modal is open
 * @param {Function} props.onClose - Callback when modal closes
 * @param {Function} props.onSuccess - Callback when linking is successful
 */
const LinkOrphanInvoicesModal = ({ open, onClose, onSuccess }) => {
  const { isMobile } = useDeviceType();
  const [loading, setLoading] = useState(false);
  const [binding, setBinding] = useState(false);
  const [error, setError] = useState(null);
  const [orphans, setOrphans] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedJobs, setSelectedJobs] = useState(new Map()); // invoiceId -> order object
  const [mode, setMode] = useState("soft"); // "soft" or "hard"
  const [bindingResults, setBindingResults] = useState(new Map()); // invoiceId -> result

  // Load orphan invoices when modal opens
  useEffect(() => {
    if (open) {
      loadOrphanInvoices();
      // Reset state
      setSelectedJobs(new Map());
      setBindingResults(new Map());
      setError(null);
      setMode("soft");
    }
  }, [open]);

  const loadOrphanInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getOrphanInvoices({ limit: 100, skip: 0 });
      if (result.success) {
        setOrphans(result.data || []);
        setTotal(result.total || 0);
      } else {
        setError(result.error || "Failed to load orphan invoices");
      }
    } catch (err) {
      setError(err.message || "Failed to load orphan invoices");
      logError("Error loading orphan invoices", { error: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleJobSelect = (invoiceId, order) => {
    setSelectedJobs((prev) => {
      const next = new Map(prev);
      if (order) {
        next.set(invoiceId, order);
      } else {
        next.delete(invoiceId);
      }
      return next;
    });
    // Clear any previous binding result for this invoice
    setBindingResults((prev) => {
      const next = new Map(prev);
      next.delete(invoiceId);
      return next;
    });
  };

  const handleBindAll = async () => {
    const bindings = [];
    selectedJobs.forEach((order, invoiceId) => {
      const jobId = order.id || order.jobId;
      if (jobId) {
        bindings.push({ invoiceId, jobId });
      }
    });

    if (bindings.length === 0) {
      setError("Please select at least one job to link");
      return;
    }

    try {
      setBinding(true);
      setError(null);

      const result = await bindInvoicesToJobs(bindings, mode);
      if (result.success) {
        // Store results per invoice
        const resultsMap = new Map();
        result.results.forEach((r) => {
          resultsMap.set(r.invoiceId, r);
        });
        setBindingResults(resultsMap);

        logInfo("Invoices bound to jobs", {
          count: bindings.length,
          mode,
          summary: result.summary,
        });

        // Call success callback after a short delay to show results
        setTimeout(() => {
          if (onSuccess) {
            onSuccess(result);
          }
          handleClose();
        }, 1500);
      } else {
        setError(result.error || "Failed to bind invoices to jobs");
        // Still show individual results if available
        if (result.results && result.results.length > 0) {
          const resultsMap = new Map();
          result.results.forEach((r) => {
            resultsMap.set(r.invoiceId, r);
          });
          setBindingResults(resultsMap);
        }
      }
    } catch (err) {
      setError(err.message || "Failed to bind invoices to jobs");
      logError("Error binding invoices to jobs", { error: err.message });
    } finally {
      setBinding(false);
    }
  };

  const handleBindSingle = async (invoiceId) => {
    const order = selectedJobs.get(invoiceId);
    if (!order) {
      setError("Please select a job for this invoice");
      return;
    }

    const jobId = order.id || order.jobId;
    if (!jobId) {
      setError("Invalid job selected");
      return;
    }

    try {
      setBinding(true);
      setError(null);

      const result = await bindInvoicesToJobs([{ invoiceId, jobId }], mode);
      if (result.success && result.results.length > 0) {
        const bindingResult = result.results[0];
        setBindingResults((prev) => {
          const next = new Map(prev);
          next.set(invoiceId, bindingResult);
          return next;
        });

        logInfo("Invoice bound to job", {
          invoiceId,
          jobId,
          mode: bindingResult.appliedMode,
        });

        // Refresh orphans list after successful binding
        setTimeout(() => {
          loadOrphanInvoices();
        }, 500);
      } else {
        setError(result.error || "Failed to bind invoice to job");
      }
    } catch (err) {
      setError(err.message || "Failed to bind invoice to job");
      logError("Error binding invoice to job", { invoiceId, error: err.message });
    } finally {
      setBinding(false);
    }
  };

  const handleClose = () => {
    setSelectedJobs(new Map());
    setBindingResults(new Map());
    setError(null);
    onClose();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-ZA", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "Invalid date";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "success";
      case "partial":
        return "warning";
      case "overdue":
        return "error";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const filledCount = selectedJobs.size;
  const canBindAll = filledCount > 0 && !binding;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <LinkIcon />
            <Typography variant="h6">Link Orphan Invoices</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading && orphans.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading orphan invoices...
            </Typography>
          </Box>
        ) : orphans.length === 0 ? (
          <Alert severity="info">
            No orphan invoices found. All invoices are linked to jobs.
          </Alert>
        ) : (
          <>
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">
                Found {total} orphan invoice{total !== 1 ? "s" : ""} (missing jobId or job doesn't exist)
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={mode === "hard"}
                    onChange={(e) => setMode(e.target.checked ? "hard" : "soft")}
                    disabled={binding}
                  />
                }
                label={
                  <Typography variant="body2">
                    Hard link (update invoice document) - Only for unlocked invoices
                  </Typography>
                }
                sx={{ mt: 1 }}
              />
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Toolbar>
                <Typography variant="h6" sx={{ flex: 1 }}>
                  Orphan Invoices
                </Typography>
                {filledCount > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {filledCount} job{filledCount !== 1 ? "s" : ""} selected
                  </Typography>
                )}
              </Toolbar>
              <Table size={isMobile ? "small" : "medium"}>
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice ID</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Job / Order</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orphans.map((invoice) => {
                    const selectedOrder = selectedJobs.get(invoice.id);
                    const bindingResult = bindingResults.get(invoice.id);
                    const isBound = bindingResult?.success === true;

                    return (
                      <TableRow key={invoice.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {invoice.id}
                          </Typography>
                          {invoice.isLocked && (
                            <Chip
                              label="Locked"
                              size="small"
                              color="warning"
                              sx={{ mt: 0.5 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {invoice.customerName || "N/A"}
                          </Typography>
                          {invoice.customerPhone && (
                            <Typography variant="caption" color="text.secondary">
                              {invoice.customerPhone}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatCurrency(invoice.total)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={invoice.status}
                            size="small"
                            color={getStatusColor(invoice.status)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(invoice.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {isBound ? (
                            <Box display="flex" alignItems="center" gap={1}>
                              <CheckCircle color="success" fontSize="small" />
                              <Typography variant="body2" color="success.main">
                                Linked ({bindingResult.appliedMode})
                              </Typography>
                            </Box>
                          ) : bindingResult?.success === false ? (
                            <Box display="flex" alignItems="center" gap={1}>
                              <ErrorIcon color="error" fontSize="small" />
                              <Typography variant="body2" color="error.main">
                                Failed
                              </Typography>
                            </Box>
                          ) : (
                            <Box sx={{ minWidth: 250 }}>
                              <OrderSearchAutocomplete
                                value={selectedOrder}
                                onChange={(order) => handleJobSelect(invoice.id, order)}
                                label="Select Job"
                                disabled={binding || isBound}
                                restrictToCustomer={false}
                              />
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          {isBound ? (
                            <Chip label="Linked" color="success" size="small" />
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleBindSingle(invoice.id)}
                              disabled={
                                binding ||
                                !selectedOrder ||
                                invoice.isLocked && mode === "hard"
                              }
                              startIcon={<LinkIcon />}
                            >
                              Link
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={binding}>
          Close
        </Button>
        {canBindAll && (
          <Button
            onClick={handleBindAll}
            variant="contained"
            disabled={binding}
            startIcon={binding ? <CircularProgress size={16} /> : <LinkIcon />}
          >
            {binding ? "Linking..." : `Link All (${filledCount})`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LinkOrphanInvoicesModal;
